<template>
  <ul class="national-parks scrollable" :class="{ desktop: $isDesktop }">
    <li v-for="(park, index) of nationalParks" :key="index">
      <pu-search-link
        :key="park.name"
        :hierarchy-path="park.url"
        :modify-filters="{ categoryIds: undefined, facets: undefined }"
        category="campsites"
        exclude-type="newLocation"
        @click="$emit('nationalParkClicked')"
      >
        <img
          v-if="props.withImage"
          loading="lazy"
          :src="park.image.photo"
          :alt="park.name"
          width="58"
          height="58"
          class="picture"
        />
        <span class="name">{{ park.name }}</span>
      </pu-search-link>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { useRoute } from 'nuxt/app'
import { getNationalParks } from './fetch'
import { isAbortError } from '~/utility/isAbortError'

const props = withDefaults(
  defineProps<{
    withImage?: boolean
  }>(),
  { withImage: true },
)

const route = useRoute()
const lang = route.params.lang || 'en-gb'
const { data: nationalParks, error } = await useAsyncData(
  `nationalParks-${lang}`,
  () => {
    return getNationalParks(route.params.lang || '')
  },
  // avoid duplicate requests server and client
  {
    server: true,
    lazy: process.client, // Defer fetching until client-side execution
    dedupe: 'cancel',
    getCachedData: (key, nuxt) => nuxt.payload.data[key] ?? undefined,
  },
)

if (error.value) {
  // Filter out AbortErrors from navigation-based request cancellation
  if (!isAbortError(error.value)) {
    console.error(error.value)
  }
}
</script>

<style scoped>
ul {
  display: grid;
  grid-auto-rows: auto;
  grid-template-columns: repeat(5, 56vw);
  column-gap: 1em;
  row-gap: 1em;
}

a {
  display: flex;
  align-items: center;
  text-decoration: none;
}

a .name {
  font-size: var(--body);
  color: var(--colour-wcag-blue);
}

.picture {
  width: 3.25em;
  height: 3.25em;
  overflow: hidden;
  flex-shrink: 0;
  border-radius: 6em;
  margin-right: 0.5em;
}

/* desktop & tablet */
ul.desktop {
  gap: 2em;
  grid-template-columns: repeat(auto-fit, minmax(18em, 1fr));
  overflow: unset !important;
}

.desktop .picture {
  width: 5em;
  height: 5em;
  overflow: hidden;
  flex-shrink: 0;
  border-radius: 5em;
  margin-right: 1em;
  transition: box-shadow 0.15s ease-out;
}

.desktop a .name {
  font-size: 1.3em;
}

.desktop a {
  transition: box-shadow 0.15s ease-out;
  border-radius: 6em;
}

.desktop a:hover,
.desktop a:focus-visible {
  outline: none;
  box-shadow: 0 0 0 7px var(--focus-ring-colour);
}
</style>
